import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import NavBarListItem from 'components/NavKit/NavBarListItem'
import BodyText from 'LuxKit/Typography/BodyText'
import useMerchandiseBrandsList from 'hooks/Merchandise/useMerchandiseBrandsList'
import useMerchandiseBrands from 'hooks/Merchandise/useMerchandiseBrands'
import { getMerchandiseBrandSearchUrl } from '../merchandiseUtils'
import IconPlaceholder from './components/IconPlaceholder'
import { useDrawerTracking, useMegaMenuTracking } from 'analytics/snowplow/events/megaMenuEvents'

const BrandsSection = styled.div`
  display: flex;
  flex-direction: column;
  padding-inline: ${rem(8)};
`

const StyledModalHeader = styled(ModalHeader)`
  padding: ${rem(16)} ${rem(20)};
`

const StyledNavBarListItem = styled(NavBarListItem)`
  padding-block: ${rem(8)};
  padding-inline: ${rem(0)};
  gap: ${rem(8)};
`

function MerchandiseBrandsDrawer() {
  const intl = useIntl()
  const { onClose, trackItemClick: trackBrandClick } = useDrawerTracking('brand')
  const trackViewAllClick = useMegaMenuTracking('view_all_brands', onClose)

  const [topBrandIds, fetchingTopBrandsList] = useMerchandiseBrandsList({
    sortBy: 'top',
    limit: 20,
  })

  const [topBrands, fetchingTopBrands] = useMerchandiseBrands(topBrandIds)

  const sortedBrands = useMemo(() => {
    if (!topBrands) return []
    return [...topBrands].sort((a, b) => a.name.localeCompare(b.name))
  }, [topBrands])

  const isFetching = fetchingTopBrandsList || fetchingTopBrands

  return (
    <ModalBase mode="drawer">
      <StyledModalHeader
        title={intl.formatMessage({
          id: 'IV7/auXkNl',
          defaultMessage: 'Brands',
          description: 'Title for merchandise brands drawer',
        })}
      />
      <ModalBody>
        <ModalContent flush>
          <BrandsSection>
            <StyledNavBarListItem
              title={<BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'kgaadR0P0K',
                  defaultMessage: 'All Brands A-Z',
                  description: 'Link text to view all brands sorted alphabetically',
                })}
              </BodyText>}
              to="/shop/brands"
              onClick={() => trackViewAllClick({ id: 'view_all_brands', name: 'All Brands A-Z' })}
              endIcon={<IconPlaceholder size={24} />}
            />

            {isFetching && (
              <>
                {Array.from({ length: 10 }).map((_, index) => (
                  <StyledNavBarListItem
                    key={`skeleton-${index}`}
                    title={intl.formatMessage({
                      id: 'tuRaZkbrLq',
                      defaultMessage: 'Loading...',
                      description: 'Loading message shown while brands are being fetched',
                    })}
                    nonInteractable
                    endIcon={<IconPlaceholder size={24} />}
                  />
                ))}
              </>
            )}

            {!isFetching && sortedBrands.map((brand, index) => (
              <StyledNavBarListItem
                key={brand.id}
                title={brand.name}
                to={getMerchandiseBrandSearchUrl(brand)}
                onClick={() => trackBrandClick(brand, index)}
                endIcon={<IconPlaceholder size={24} />}
              />
            ))}
          </BrandsSection>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default MerchandiseBrandsDrawer
